import { useCallback, useMemo, useState } from 'react';
import { FormikHelpers } from 'formik';
import useBrandActions from 'data/hooks/useBrandActions';
import { openNewMessage } from 'utils/intercom';
import useBrands from 'data/hooks/useBrands';
import { today, formatDate } from '../../DatePicker/utils';
import { initialValues, validationSchema } from './constants';

type FormValues = typeof initialValues;

type ModalState = 'normal' | 'custom-confirm' | 'close-confirm';

const useConnect = (
  brandId: string | undefined,
  integrationId: string | undefined,
  handleClose: ((submitted: boolean) => void) | undefined,
) => {
  const { pause } = useBrandActions();
  const [modalState, setModalState] = useState<ModalState>('normal');

  const changeToNormal = useCallback(() => {
    setModalState('normal');
  }, [setModalState]);

  const changeToCustomConfirm = useCallback(() => {
    setModalState('custom-confirm');
  }, [setModalState]);

  const changeToCloseConfirm = useCallback(() => {
    setModalState('close-confirm');
  }, [setModalState]);

  const { brands } = useBrands();

  const selectedBrand = useMemo(
    () => brands.find((brand) => brand.id === brandId),
    [brands, brandId],
  );

  const selectedIntegration = useMemo(
    () => selectedBrand?.integrations.find((i) => i.id === integrationId),
    [selectedBrand, integrationId],
  );

  const handleRequestCustomPeriod = useCallback(() => {
    let msg = `Please put all brands into busy mode. The reason is ...`;
    if (selectedBrand != null && selectedIntegration == null) {
      msg = `Please put stores of ${selectedBrand.name} into busy mode. The reason is ...`;
    } else if (selectedBrand != null && selectedIntegration != null) {
      msg = `Please put store of ${selectedBrand.name} into busy mode. The reason is ...`;
      if (selectedIntegration != null) {
        msg = `Please put ${selectedIntegration.name.full} of ${selectedBrand.name} into busy mode. The reason is ...`;
      }
    }

    openNewMessage(msg);

    if (handleClose != null) {
      handleClose(false);
    }
  }, [selectedBrand, selectedIntegration, handleClose]);

  const [endDate, setEndDate] = useState<Date>(today);
  const [endHour, setEndHour] = useState<string>('00:00');

  const handleSubmit = useCallback(
    async (values: FormValues, { setErrors }: FormikHelpers<FormValues>) => {
      if (values.busy_mode === 'custom') {
        return;
      }

      try {
        await pause({
          brandId,
          integrationId,
          busyModeType: values.busy_mode,
          customDate: formatDate(endDate),
          customTime: endHour,
        });
        if (handleClose !== undefined) {
          handleClose(true);
        }
      } catch (error) {
        setErrors({
          // @ts-ignore
          ...error.fields,
        });
      }
    },
    [pause, endHour, endDate, handleClose, brandId, integrationId],
  );

  return {
    handleSubmit,
    initialValues,
    validationSchema,
    modalState,
    setModalState,
    endDate,
    setEndDate,
    endHour,
    setEndHour,
    changeToNormal,
    changeToCustomConfirm,
    changeToCloseConfirm,
    handleRequestCustomPeriod,
    selectedBrand,
    selectedIntegration,
  };
};

export default useConnect;
